import React, { useContext } from "react";
import { Box, Button, Grid, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { BarChart, PieChart } from "@mui/x-charts";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "react-query";

import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";

import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import Spinner from "../../components/Spinner";
import { LanguageContext } from "../../components/LanguageContext";
import {
    getBrojProizvodaPoProdavnicama,
    getOStalaAnalitika,
} from "../../api/analyticsApi";

const DashboardPage = () => {
    const { translations } = useContext(LanguageContext) || {};
    const muiTheme = useTheme();
    const isLg = useMediaQuery(muiTheme.breakpoints.up("lg"));
    const isMd = useMediaQuery(muiTheme.breakpoints.up("md"));
    const isSm = useMediaQuery(muiTheme.breakpoints.up("sm"));

    const { data, isLoading } = useQuery({
        queryKey: ["getBrojProizvodaPoProdavnicama"],
        queryFn: getBrojProizvodaPoProdavnicama,
    });

    const { data: ostalaAnalitika, isLoading: loadingOstalaAnalitika } = useQuery({
        queryKey: ["getOStalaAnalitika"],
        queryFn: getOStalaAnalitika,
    });

    const pieSize = isLg ? 520 : isMd ? 460 : isSm ? 400 : 340;
    const innerR = isLg ? 70 : isMd ? 60 : 50;
    const outerR = isLg ? 160 : isMd ? 140 : 110;

    const quickActions = [
        {
            to: "/dobavljaci",
            icon: <LocalShippingOutlinedIcon />,
            label: translations?.adminDashboardPageSuppliers || "Suppliers",
        },
        {
            to: "/korisnici",
            icon: <PeopleAltOutlinedIcon />,
            label: translations?.adminDashboardPageUsers || "Users",
        },
        {
            to: "/prodavnice",
            icon: <StorefrontOutlinedIcon />,
            label: translations?.adminDashboardPageStores || "Stores",
        },
    ];

    return (
        <Box>
            <PageHeader
                icon={<DashboardOutlinedIcon />}
                eyebrow={translations?.dashboard || "Dashboard"}
                title={translations?.welcomeMessage || "Welcome to your analytics dashboard"}
                description={
                    translations?.otherAnalytics
                        ? null
                        : "Pregled ključnih pokazatelja sistema."
                }
            />

            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <SectionCard
                        title={translations?.otherAnalytics || "System Analytics"}
                        description="Brojevi po kategorijama u sistemu."
                    >
                        {loadingOstalaAnalitika ? (
                            <Spinner variant="inline" />
                        ) : (
                            <BarChart
                                series={[
                                    {
                                        data: [
                                            ostalaAnalitika?.data.broj_dobavljaca,
                                            ostalaAnalitika?.data.broj_prodavnica,
                                            ostalaAnalitika?.data.broj_korisnika,
                                            ostalaAnalitika?.data.broj_narudzbenica,
                                        ],
                                        color: muiTheme.palette.primary.main,
                                    },
                                ]}
                                height={360}
                                xAxis={[
                                    {
                                        data: [
                                            translations?.suppliers || "Suppliers",
                                            translations?.stores || "Stores",
                                            translations?.users || "Users",
                                            translations?.order || "Orders",
                                        ],
                                        scaleType: "band",
                                    },
                                ]}
                                margin={{ top: 16, bottom: 40, left: 50, right: 12 }}
                            />
                        )}
                    </SectionCard>
                </Grid>

                <Grid item xs={12}>
                    <SectionCard
                        title={translations?.productsByStore || "Products by Store"}
                        description="Distribucija proizvoda po prodavnicama."
                    >
                        {isLoading ? (
                            <Spinner variant="inline" />
                        ) : (
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <PieChart
                                    series={[
                                        {
                                            data: data?.data.result,
                                            highlightScope: {
                                                fade: "global",
                                                highlight: "item",
                                            },
                                            faded: {
                                                innerRadius: innerR - 10,
                                                additionalRadius: -20,
                                                color: muiTheme.palette.action.disabled,
                                            },
                                            innerRadius: innerR,
                                            outerRadius: outerR,
                                            cornerRadius: 4,
                                        },
                                    ]}
                                    height={pieSize}
                                    slotProps={{ legend: { hidden: true } }}
                                    margin={{ top: 24, bottom: 24, left: 24, right: 24 }}
                                />
                            </Box>
                        )}
                    </SectionCard>
                </Grid>

                <Grid item xs={12}>
                    <SectionCard
                        title={translations?.quickActions || "Quick Actions"}
                        dense
                    >
                        <Grid container spacing={2}>
                            {quickActions.map((qa) => (
                                <Grid item xs={12} sm={6} md={4} key={qa.to}>
                                    <Button
                                        component={RouterLink}
                                        to={qa.to}
                                        fullWidth
                                        size="large"
                                        variant="outlined"
                                        startIcon={qa.icon}
                                        sx={{ justifyContent: "flex-start", py: 1.5 }}
                                    >
                                        {qa.label}
                                    </Button>
                                </Grid>
                            ))}
                        </Grid>
                    </SectionCard>
                </Grid>
            </Grid>
        </Box>
    );
};

export default DashboardPage;